import { useState, useContext } from 'react';

import endpoints from '../../../../../../api/endpoints';
import { handleResponseError } from '../../../../../../api/handleResponseError';
import { ToastNotificationContext } from '../../../../../common/toast-notification';
import { FailureSeverity, DialogError } from '../../../../../common/business-error';
import { fetchWithTokenRefresh } from '../../../../../../api/fetchWithTokenRefresh';

import { BUSINESS_ERRORS } from '../interfaces';
import { getReSendOtpBusinessError } from '../businessErrors';

interface HandleReSendOtpProps {
  onFailure: (failureSeverity: FailureSeverity | 'active') => void;
}

export const useReSendOtp = () => {
  const [reSendOtpError, setReSendOtpError] = useState<DialogError>(null);
  const [reSendOtpLoading, setReSendOtpLoading] = useState<boolean>(false);
  const [reSendOtpBlocked, setReSendOtpBlocked] = useState<boolean>(false);
  const { openToast } = useContext(ToastNotificationContext);
  const [resendMsisdn, setResendMsisdn] = useState<string>();
  const [resendShortcode, setResendShortcode] = useState<string>();

  const handleReSendOtp = async ({ onFailure }: HandleReSendOtpProps) => {
    try {
      setReSendOtpLoading(true);
      setReSendOtpBlocked(false);
      setReSendOtpError(null);

      const response = await fetchWithTokenRefresh(endpoints.clientCreateOtpResend, { method: 'POST' });

      if (response.status !== 200) {
        await handleResponseError(response, true);
      }

      const jsonData = await response.json();
      setResendMsisdn(jsonData.otp.msisdn);
      setResendShortcode(jsonData.otp.ussdShortcodeToDial);

      openToast({
        message: 'OTP has been successfully sent to your phone',
        severity: 'success'
      });
    } catch (error) {
      if (error.code === BUSINESS_ERRORS.OTP_RESEND_BLOCKED) {
        const penaltyTime = error.nextAttemptTime ? error.nextAttemptTime * 1000 : 0;
        setReSendOtpBlocked(true);

        setTimeout(() => {
          setReSendOtpBlocked(false);
          onFailure('active');
          setReSendOtpError(null);
        }, penaltyTime);
      }

      const { message, severity } = getReSendOtpBusinessError(error.code, { duration: error.nextAttemptTime });

      onFailure(severity);
      setReSendOtpError({ message, severity });
    } finally {
      setReSendOtpLoading(false);
    }
  };

  return { reSendOtpError, reSendOtpLoading, reSendOtpBlocked, handleReSendOtp, resendMsisdn, resendShortcode };
};
